/**
 * Maandrapport's addresses: Overzicht at `/apps/maandrapport`, every other tab at `/apps/maandrapport/<tab>`, a
 * client's page at `/apps/maandrapport/klanten/<partner id>`, and the range in the query (`?periode=30-dagen`, or
 * `?van=…&tot=…`), so it stays when someone moves to another tab and a link opens the same range. The detail open in
 * a popup is in the query too (`&detail=activity:transport`), so Back closes it.
 */

import {
  ACTIVITIES,
  AT_PARAM,
  BACK_AT_PARAM,
  BACK_DETAIL_PARAM,
  BACK_PARAM,
  BANDS,
  CLIENT_SEGMENT,
  DETAIL_KINDS,
  DETAIL_PARAM,
  type DetailKind,
  FINDINGS,
  MAANDRAPPORT_DEFAULT_TAB,
  MAANDRAPPORT_PATH,
  MAANDRAPPORT_TABS,
  type MaandrapportTab,
  REGIONS,
  UNBILLED_CLASSES,
} from "./contract";

export function isMaandrapportTab(value: unknown): value is MaandrapportTab {
  return typeof value === "string" && (MAANDRAPPORT_TABS as readonly string[]).includes(value);
}

/** A tab's path: Overzicht at the app's own path, the others under it, a client's page under Klanten. */
export function maandrapportTabPath(tab: MaandrapportTab, client: number | null = null): string {
  if (client !== null && tab === CLIENT_SEGMENT) {
    return `${MAANDRAPPORT_PATH}/${CLIENT_SEGMENT}/${client}`;
  }
  return tab === MAANDRAPPORT_DEFAULT_TAB ? MAANDRAPPORT_PATH : `${MAANDRAPPORT_PATH}/${tab}`;
}

export type MaandrapportRoute =
  | { readonly kind: "page"; readonly tab: MaandrapportTab; readonly client: number | null }
  | { readonly kind: "redirect"; readonly to: { pathname: string; search: string } };

/** A partner id in a path: a positive whole number without leading zeros. */
function clientId(segment: string | undefined): number | null {
  if (!segment || !/^[1-9]\d{0,11}$/.test(segment)) {
    return null;
  }
  return Number(segment);
}

/**
 * What an app address shows: a tab, a client's page, or the nearest page instead (a replace, so Back skips it):
 * Overzicht for `/apps/maandrapport/overzicht` and any tab that does not exist, Klanten for a client segment that is
 * no partner id, the tab alone for anything after another tab. The query stays.
 */
export function resolveMaandrapportRoute(
  pathTab: string | undefined,
  search: string,
  pathClient?: string,
): MaandrapportRoute {
  const params = new URLSearchParams(search);
  const rest = params.toString();
  const to = (pathname: string): MaandrapportRoute => ({
    kind: "redirect",
    to: { pathname, search: rest ? `?${rest}` : "" },
  });
  if (pathTab === undefined) {
    return { kind: "page", tab: MAANDRAPPORT_DEFAULT_TAB, client: null };
  }
  if (!isMaandrapportTab(pathTab) || pathTab === MAANDRAPPORT_DEFAULT_TAB) {
    return to(MAANDRAPPORT_PATH);
  }
  if (pathClient === undefined) {
    return { kind: "page", tab: pathTab, client: null };
  }
  const id = pathTab === CLIENT_SEGMENT ? clientId(pathClient) : null;
  if (id === null) {
    return to(maandrapportTabPath(pathTab));
  }
  return { kind: "page", tab: pathTab, client: id };
}

/** The detail an address opens: its kind, key and moment, or null. */
export interface DetailRef {
  readonly kind: DetailKind;
  readonly key: string;
  /** A returning or new clients' list of the comparison. */
  readonly last: boolean;
}

/** The details that have a comparison of their own to show (`&bij=vorig`). */
export const hasMoment = (kind: DetailKind): boolean =>
  kind === "returning" || kind === "new_clients";

export function isDetailKind(value: unknown): value is DetailKind {
  return typeof value === "string" && (DETAIL_KINDS as readonly string[]).includes(value);
}

const among = (list: readonly string[], value: string): boolean => list.includes(value);

/**
 * Whether a key is one its kind knows: an old link's key (an activity the app no longer shows, a band of an earlier
 * version) or a typed one opens no window. A crane type is any text: the backend answers one it does not know.
 */
export function knownDetailKey(kind: DetailKind, key: string): boolean {
  switch (kind) {
    case "activity":
      return among(ACTIVITIES, key);
    case "region":
      return among(REGIONS, key);
    case "ageing":
      return key === "open" || key === "overdue" || among(BANDS, key);
    case "due_month":
      return /^\d{4}-(0[1-9]|1[0-2])$/.test(key);
    case "concentration":
      return key === "alles" || among(ACTIVITIES, key);
    case "transports":
      return key === "BK" || key === "TK";
    case "unbilled": {
      const [what = "", cls, ...rest] = key.split(":");
      if (rest.length) {
        return false;
      }
      if (cls === undefined) {
        return among(FINDINGS, what) || among(UNBILLED_CLASSES, what);
      }
      return (what === "alle" || among(FINDINGS, what)) && among(UNBILLED_CLASSES, cls);
    }
    case "returning":
      return key === "away" || key === "back";
    case "new_clients":
      return key === "new";
    case "invoice":
      return /^[1-9]\d{0,11}$/.test(key);
    case "rentals":
      return key.trim().length > 0;
    // A client's page, the Klanten tab's list and the late payers per invoice draw on a page, never in a popup.
    case "client":
    case "clients":
    case "due_invoices":
      return false;
  }
}

/** The detail in a query string: `detail=<kind>:<key>` (the key may hold a colon, "geleverd:zonder_spoor"). */
export function detailOf(params: URLSearchParams): DetailRef | null {
  const raw = params.get(DETAIL_PARAM);
  if (!raw) {
    return null;
  }
  const at = raw.indexOf(":");
  const kind = at < 0 ? raw : raw.slice(0, at);
  const key = at < 0 ? "" : raw.slice(at + 1);
  if (!isDetailKind(kind) || kind === "client" || !key || !knownDetailKey(kind, key)) {
    return null;
  }
  return { kind, key, last: hasMoment(kind) && params.get(AT_PARAM) === "vorig" };
}

/** The query string with a detail open, or with none (null). Every other key stays. */
export function withDetail(params: URLSearchParams, detail: DetailRef | null): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(DETAIL_PARAM);
  next.delete(AT_PARAM);
  if (detail) {
    next.set(DETAIL_PARAM, `${detail.kind}:${detail.key}`);
    if (hasMoment(detail.kind) && detail.last) {
      next.set(AT_PARAM, "vorig");
    }
  }
  return next;
}

/** Two details the same. */
export function sameDetail(a: DetailRef | null, b: DetailRef | null): boolean {
  return (
    a === b ||
    (a !== null && b !== null && a.kind === b.kind && a.key === b.key && a.last === b.last)
  );
}

/** What a detail's figures were read for: the snapshot, the range and the client page under the window. */
export interface DetailContext {
  readonly snapshot: string;
  readonly from: string;
  readonly to: string;
  readonly client: number | null;
}

export function sameContext(a: DetailContext | null, b: DetailContext | null): boolean {
  return (
    a !== null &&
    b !== null &&
    a.snapshot === b.snapshot &&
    a.from === b.from &&
    a.to === b.to &&
    a.client === b.client
  );
}

/**
 * What the window does when the address or the page under it changed: `keep` the detail it shows, `close`, `open`
 * another one, or `reload` the one it shows because its figures are of another snapshot or range (a recompute that
 * completed, Back to the same popup over another range). A reload keeps the details under it for "← Terug".
 */
export function detailAction(
  want: DetailRef | null,
  shown: DetailRef | null,
  open: boolean,
  shownFor: DetailContext | null,
  now: DetailContext | null,
): "keep" | "close" | "open" | "reload" {
  if (want === null) {
    return shown === null ? "keep" : "close";
  }
  if (!open || !sameDetail(want, shown)) {
    return "open";
  }
  return sameContext(shownFor, now) ? "keep" : "reload";
}

/** The query string without the detail: what decides the page under the popup. */
export function pageQuery(params: URLSearchParams | string): string {
  return withDetail(new URLSearchParams(params), null).toString();
}

/** Where a client's page leads back to: the tab it was opened from, and the popup that was open over it. */
export interface BackRef {
  readonly tab: MaandrapportTab;
  readonly detail: DetailRef | null;
}

/** The origin a client's page address carries, or null (its back link then leads to Klanten). */
export function backOf(params: URLSearchParams): BackRef | null {
  const tab = params.get(BACK_PARAM);
  if (!isMaandrapportTab(tab)) {
    return null;
  }
  const raw = params.get(BACK_DETAIL_PARAM);
  const detail = raw
    ? detailOf(
        new URLSearchParams({ [DETAIL_PARAM]: raw, [AT_PARAM]: params.get(BACK_AT_PARAM) ?? "" }),
      )
    : null;
  return { tab, detail };
}

/**
 * The query string with an origin, or with none (null). The origin's popup moves into its own key: a client's page
 * shows no popup of its own from the address.
 */
export function withBack(params: URLSearchParams, back: BackRef | null): URLSearchParams {
  const next = withDetail(params, null);
  next.delete(BACK_PARAM);
  next.delete(BACK_DETAIL_PARAM);
  next.delete(BACK_AT_PARAM);
  if (back) {
    next.set(BACK_PARAM, back.tab);
    if (back.detail) {
      next.set(BACK_DETAIL_PARAM, `${back.detail.kind}:${back.detail.key}`);
      if (hasMoment(back.detail.kind) && back.detail.last) {
        next.set(BACK_AT_PARAM, "vorig");
      }
    }
  }
  return next;
}

/** The query string back at the origin: its popup open again, the origin's keys gone. */
export function atBack(params: URLSearchParams, back: BackRef): URLSearchParams {
  return withDetail(withBack(params, null), back.detail);
}
